import { assert } from '@disy/lib/custom-error';

import i18n from './move-attribute-in-bundle.properties';

/**
 * Creates menu items for moving an AttributeList item: to top, up, down or to bottom of the list.
 *
 * @param {AttributeListController} controller - The controller to apply the actions to
 * @param {AttributeIdLike} attributeId - The id of the attribute to move
 * @return {MenuItem[]} The created menu items
 */
export function createMoveAttributeInBundleMenuItems(controller, attributeId) {
  const attributeIds = Array.from(controller.attributeIds);
  const currentIndex = attributeIds.indexOf(attributeId);

  assert(attributeIds.length > 0, 'Current attributeIds must not be empty');
  assert(currentIndex !== -1, `AttributeId '${attributeId}' not present in attribute ids: ${attributeIds.join(', ')}`);

  function createReorderMenuItem(newIndex, label) {
    return {
      label,
      execute: () => {
        attributeIds.splice(currentIndex, 1);
        attributeIds.splice(newIndex, 0, attributeId);
        controller.reorder(attributeIds);
      },
      disabled: currentIndex === newIndex,
    };
  }

  /**
   * Used to find target indizes within the range of [0, attributeId.length[.
   *
   * @param {number} index - Index to clamp
   * @return {number} Index clamped to [0, attributeId.length[
   */
  function clamp(index) {
    return Math.min(Math.max(index, 0), attributeIds.length - 1);
  }

  return [
    createReorderMenuItem(0, i18n('attribute.action.title.move-top')),
    createReorderMenuItem(clamp(currentIndex - 1), i18n('attribute.action.title.move-up')),
    createReorderMenuItem(clamp(currentIndex + 1), i18n('attribute.action.title.move-down')),
    createReorderMenuItem(clamp(attributeIds.length - 1), i18n('attribute.action.title.move-bottom')),
  ];
}

/**
 * Creates sub menu entry with actions to move attribute item up, to top, down or to bottom of the list.
 *
 * @param {AttributeListController} controller - The controller to apply the actions to
 * @param {AttributeIdLike} attributeId - The id of the attribute to move
 * @return {MenuItem} The created sub menu entry
 */
export function createMoveAttributeInBundleSubMenu(controller, attributeId) {
  return {
    label: i18n('attribute.sorting.submenu.title'),
    subMenu: createMoveAttributeInBundleMenuItems(controller, attributeId),
  };
}
